/**
 * Роль-зависимый экран «Подписка и счета» на общем ключе billing (один
 * поддомен на отель, роль-роутинг).
 *
 *  - Владелец (tenant_owner)         → «Подписка» (тариф+счета подписки, оплата
 *                                       Т-Банк) + вкладка «Счета гостей».
 *  - Менеджер/персонал               → «Все счета» гостей (фолио, действия).
 *  - Гость                           → «Мои счета» (свои, read-only).
 *
 * Подписка — только владелец (GET /v1/subscriptions/me owner-gated на бэке),
 * поэтому менеджер остаётся на гостевых фолио (его /me вернул бы 403).
 * Гостевые фолио НЕ теряются: у владельца они доступны вкладкой.
 */
import { useState } from 'react';
import { usePlatformMe } from '../../shared/api/usePlatformMe.js';
import { AllFoliosPage } from './AllFoliosPage.js';
import { MyFolioPage } from './MyFolioPage.js';
import { SubscriptionPage } from './SubscriptionPage.js';
import { InvoicesLegalPage } from './InvoicesLegalPage.js';
import { actorIsBillingStaff } from './format.js';

const OWNER_ROLES = new Set<string>(['tenant_owner', 'owner']);

function actorIsOwner(roles: readonly string[]): boolean {
  for (const r of roles) {
    if (OWNER_ROLES.has(r)) return true;
  }
  return false;
}

type OwnerTab = 'subscription' | 'invoices' | 'folios';

/**
 * Владелец: три вкладки-экрана — «Подписка» (§13.1) + «Счета» юр.лицу (§13.2)
 * + «Счета гостей» (фолио, НЕ теряем). Каждая вкладка — отдельный экран.
 */
function OwnerBilling(): JSX.Element {
  const [tab, setTab] = useState<OwnerTab>('subscription');
  const tabBtn = (id: OwnerTab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === id}
      className={`ta-tab${tab === id ? ' ta-tab--active' : ''}`}
      data-testid={`billing-tab-${id}`}
      onClick={() => setTab(id)}
    >
      {label}
    </button>
  );
  return (
    <div data-testid="owner-billing">
      <div className="ta-tabs" role="tablist" aria-label="Подписка и счета">
        {tabBtn('subscription', 'Подписка')}
        {tabBtn('invoices', 'Счета')}
        {tabBtn('folios', 'Счета гостей')}
      </div>
      {tab === 'subscription' ? (
        <SubscriptionPage />
      ) : tab === 'invoices' ? (
        <InvoicesLegalPage />
      ) : (
        <AllFoliosPage />
      )}
    </div>
  );
}

export function BillingByRole(): JSX.Element {
  const { data } = usePlatformMe();
  const roles = data?.actor.roles ?? [];
  if (actorIsOwner(roles)) {
    return <OwnerBilling />;
  }
  if (actorIsBillingStaff(roles)) {
    return <AllFoliosPage />;
  }
  return <MyFolioPage />;
}
